import errors from '@lecab/errors';
import escapeRegExp from 'lodash/escapeRegExp';
import {
    Vehicle,
    Status,
    PaymentClearanceStatus,
    PaymentTypes,
    Stop,
    StopType,
    POI,
    Address,
    PoiStop,
    MeetingPoint,
    PaymentCard,
    PaymentGroupSetting,
    AddressType,
    Ride,
    PaymentMethod,
    IDContractType,
    CompanyGuest,
    BaseUser,
    BaseUserId,
    Policy,
    Favorite,
    Coworker,
    Plan,
    ContractInfos,
    ServiceStatus,
    Quote,
    Service,
    GetServicesOutput,
    QuoteVoucher,
    PaymentBreakdown,
    CoverageVoucher,
    CreateVoucherInput,
} from '../core/apiTypes';
import { getLocalError, stringsRes } from '../resources/strings';
import { addTimeToDate, unix } from './date';
import { getUser } from '../localStorageAPI';
import { UserGroupType } from '../core/modules/currentGroup/type';
import { Animation, IconAxel, IconCommon } from '../resources/images';
import { DAYS, SHORT_DAY_LENGTH } from './constants';
import { joinWithDash } from './strings';

//= ====================================//
//= ============== Status ===============//
//= ====================================//

export function isBookingFinishedOrCancelled(status: Status): boolean {
    return [Status.FINISHED, Status.CANCELLATION_NO_CHARGE, Status.CANCELLATION_WITH_CHARGE].includes(status);
}

export function isBookingCancelled(status: Status): boolean {
    return [Status.CANCELLATION_NO_CHARGE, Status.CANCELLATION_WITH_CHARGE].includes(status);
}

export function isBookingCancelledNoCharge(status: Status): boolean {
    return [Status.CANCELLATION_NO_CHARGE].includes(status);
}

export function isBookingFinishedNotCanceled(status: Status): boolean {
    return [Status.FINISHED].includes(status);
}

export function isBookingForLater(status: Status): boolean {
    return [Status.CREATED].includes(status);
}
export function isBookingLoading(status: Status): boolean {
    return [Status.CREATED, Status.READY_TO_DISPATCH, Status.PROCESSING, Status.RECEIVED].includes(status);
}

//= =============================================//
//= ====== Paiement : verdict de clearance =======//
//= =============================================//
//
// LT-1884. Le verdict vit dans `payment_clearance_status`, a cote du statut de
// course et jamais a sa place : une course annulee GARDE son verdict, et c'est
// precisement ce qui permet de dire pourquoi elle a ete annulee (LT-2056).
//
// `null`/absent = course hors perimetre clearance. Aucune UI ne se declenche
// la-dessus : seule une valeur explicite compte.
//
// READY et PREAUTH_PENDING n'ont deliberement aucun helper : la validation du
// paiement est le cas normal, elle doit paraitre acquise (operateur, 17/09).
// Seuls le refus et l'annulation se montrent. Une reservation prise dix jours
// a l'avance reste d'ailleurs PREAUTH_PENDING pendant une semaine entiere
// (LT-1930) — l'afficher reviendrait a inquieter sans raison, tous les jours.

/** Le paiement a ete refuse — la course est a regulariser, ou a ete annulee pour ca. */
export function isClearanceBlocked(ride: Ride): boolean {
    return ride.payment_clearance_status === PaymentClearanceStatus.BLOCKED;
}

/**
 * La course attend une regularisation : le paiement est refuse ET la course
 * est encore vivante. Une course deja annulee n'attend plus rien, elle releve
 * de isRideCancelledForPayment.
 */
export function isRideAwaitingPaymentFix(ride: Ride): boolean {
    return isClearanceBlocked(ride) && !isBookingCancelled(ride.status);
}

/** La course a ete annulee FAUTE de paiement valide, et non pour une autre raison. */
export function isRideCancelledForPayment(ride: Ride): boolean {
    return isClearanceBlocked(ride) && isBookingCancelled(ride.status);
}

/**
 * Les courses a regulariser parmi celles que l'application connait deja.
 * Calcule cote client sur la liste deja sondee — aucun appel reseau de plus.
 * Un lot de reservations cree automatiquement partage son moyen de paiement,
 * donc echoue en bloc : c'est pour ca que l'alerte est agregee et pas une
 * fenetre par course (jusqu'a 40 courses vues dans la meme minute en prod).
 */
export function getRidesAwaitingPaymentFix(rides: Ride[] | null): Ride[] {
    return (rides ?? []).filter(isRideAwaitingPaymentFix);
}

export function convertPaymentType(paymentType: PaymentTypes): string {
    switch (paymentType) {
        case PaymentTypes.APPLE_PAY:
            return stringsRes.paymentType.applePay;
        case PaymentTypes.CASH:
            return stringsRes.paymentType.cash;
        case PaymentTypes.CREDIT_CARD:
            return stringsRes.paymentType.creditCard;
        case PaymentTypes.INVOICE:
            return stringsRes.paymentType.invoice;
        case PaymentTypes.PAYPAL:
            return stringsRes.paymentType.paypal;
        default:
            return '';
    }
}

export function convertStatus(status: Status): string {
    switch (status) {
        case Status.CREATED:
            return stringsRes.status.created;
        case Status.READY_TO_DISPATCH:
            return stringsRes.status.readyToDispatch;
        case Status.PROCESSING:
            return stringsRes.status.processing;
        case Status.RECEIVED:
            return stringsRes.status.received;
        case Status.ACCEPTED:
            return stringsRes.status.accepted;
        case Status.AT_PICKUP:
            return stringsRes.status.atPickup;
        case Status.PASSENGER_ON_BOARD:
            return stringsRes.status.passengerOnBoard;
        case Status.AT_DROP_OFF:
            return stringsRes.status.atDropOff;
        case Status.FINISHED:
            return stringsRes.status.finished;
        case Status.CANCELLATION_NO_CHARGE:
            return stringsRes.status.cancellationNoCharge;
        case Status.CANCELLATION_WITH_CHARGE:
            return stringsRes.status.cancellationWithCharge;
        default:
            return '';
    }
}

export function convertStatusForSoon(status: Status, time?: number): string {
    switch (status) {
        case Status.CREATED:
        case Status.READY_TO_DISPATCH:
            return stringsRes.bookingSoon.status.reserved;
        case Status.PROCESSING:
        case Status.RECEIVED:
            return stringsRes.bookingSoon.status.processing;
        case Status.DISPATCH_TIMEOUT:
            return stringsRes.bookingSoon.status.fakeIncoming;
        case Status.ACCEPTED:
            return time ? stringsRes.bookingSoon.status.incomingEta : stringsRes.bookingSoon.status.incoming;
        case Status.AT_PICKUP:
            return stringsRes.bookingSoon.status.driverArrived;
        case Status.PASSENGER_ON_BOARD:
            return time ? stringsRes.bookingSoon.status.inProgressEta : stringsRes.bookingSoon.status.inProgress;
        case Status.CANCELLATION_NO_CHARGE:
        case Status.CANCELLATION_WITH_CHARGE:
            return stringsRes.bookingSoon.status.canceled;
        case Status.FINISHED:
            return stringsRes.bookingSoon.status.finished;
        // TODO define what status gives this
        // return stringsRes.bookingSoon.status.noDriver;
        default:
            return '';
    }
}

export function convertStatusForSoonSub(status: Status, time?: number): string {
    switch (status) {
        case Status.CREATED:
        case Status.READY_TO_DISPATCH:
            return '';
        case Status.PROCESSING:
        case Status.RECEIVED:
            return stringsRes.bookingSoon.status.processingSub;
        case Status.DISPATCH_TIMEOUT:
            return stringsRes.bookingSoon.status.processingSub;
        case Status.ACCEPTED:
            return stringsRes.bookingSoon.status.incomingSub;
        case Status.AT_PICKUP:
            return stringsRes.bookingSoon.status.driverArrivedSub;
        case Status.PASSENGER_ON_BOARD:
            return time
                ? stringsRes.bookingSoon.status.inProgressSub
                : stringsRes.bookingSoon.status.inProgressFallback;
        case Status.CANCELLATION_NO_CHARGE:
        case Status.CANCELLATION_WITH_CHARGE:
        case Status.FINISHED:
            return '';
        // TODO define what status gives this
        // return stringsRes.bookingSoon.status.noDriver;
        default:
            return '';
    }
}

export function convertStatusForIcon(status: Status): IconAxel {
    switch (status) {
        case Status.CREATED:
            return IconAxel.Clock;
        case Status.READY_TO_DISPATCH:
        case Status.PROCESSING:
        case Status.RECEIVED:
        case Status.DISPATCH_TIMEOUT:
        case Status.ACCEPTED:
            return IconAxel.CarFront;
        case Status.AT_PICKUP:
            return IconAxel.Localisation;
        case Status.PASSENGER_ON_BOARD:
            return IconAxel.Flag;
        case Status.CANCELLATION_NO_CHARGE:
        case Status.CANCELLATION_WITH_CHARGE:
            return IconAxel.Warning;
        case Status.FINISHED:
            return IconAxel.Check;
        default:
            return IconAxel.CarFront;
    }
}

export function convertStatusForAnimation(status: Status): Animation {
    switch (status) {
        case Status.CREATED:
        case Status.READY_TO_DISPATCH:
        case Status.PROCESSING:
        case Status.RECEIVED:
        case Status.DISPATCH_TIMEOUT:
        case Status.ACCEPTED:
            return Animation.CarFront;
        case Status.AT_PICKUP:
            return Animation.Localisation;
        case Status.PASSENGER_ON_BOARD:
            return Animation.Flag;
        case Status.CANCELLATION_NO_CHARGE:
        case Status.CANCELLATION_WITH_CHARGE:
        case Status.FINISHED:
            return null;
        default:
            return Animation.CarFront;
    }
}

// Status that will show the round pin instead of the car
export function getStartPinStatus(): Status[] {
    return [
        Status.CREATED,
        Status.READY_TO_DISPATCH,
        Status.DISPATCH_TIMEOUT,
        Status.PROCESSING,
        Status.RECEIVED,
        Status.FINISHED,
        Status.CANCELLATION_NO_CHARGE,
        Status.CANCELLATION_WITH_CHARGE,
    ];
}

export function getRedirectionTriggerStatus(): Status[] {
    return [Status.ACCEPTED, Status.AT_PICKUP, Status.PASSENGER_ON_BOARD, Status.FINISHED];
}

export function getInProgressStatus(): Status[] {
    return [
        Status.ACCEPTED,
        Status.DISPATCH_TIMEOUT,
        Status.PROCESSING,
        Status.RECEIVED,
        Status.ACCEPTED,
        Status.PASSENGER_ON_BOARD,
        Status.AT_PICKUP,
    ];
}

export function getIncomingStatus(): Status[] {
    return [Status.CREATED, Status.READY_TO_DISPATCH];
}

export function getCancellableStatus(): Status[] {
    return [
        Status.CREATED,
        Status.READY_TO_DISPATCH,
        Status.DISPATCH_TIMEOUT,
        Status.ACCEPTED,
        Status.PROCESSING,
        Status.RECEIVED,
        Status.AT_PICKUP,
    ];
}

export function getFinishedAndCancelledStatus(): Status[] {
    return [Status.FINISHED, Status.CANCELLATION_NO_CHARGE, Status.CANCELLATION_WITH_CHARGE];
}

export function getBilledStatus(): Status[] {
    return [Status.FINISHED, Status.CANCELLATION_WITH_CHARGE];
}

export function getRideTrackingStatusOrdered(): Status[] {
    return [...getInProgressStatus(), ...getIncomingStatus(), ...getFinishedAndCancelledStatus()];
}

export function getRideTrackingZoom(): Status[] {
    return [Status.ACCEPTED, Status.AT_PICKUP];
}

export function getRideTrackingZoomPU(): Status[] {
    return [Status.PROCESSING, Status.ACCEPTED];
}

export function getRideTrackingShowDO(): Status[] {
    return [
        Status.CREATED,
        Status.PASSENGER_ON_BOARD,
        Status.FINISHED,
        Status.CANCELLATION_NO_CHARGE,
        Status.CANCELLATION_WITH_CHARGE,
    ];
}

export function getSOSStatus(): Status[] {
    return [Status.AT_PICKUP, Status.PASSENGER_ON_BOARD];
}

export function getCancelReasonStatus(): Status[] {
    return [Status.DISPATCH_TIMEOUT, Status.ACCEPTED, Status.AT_PICKUP];
}

//= ====================================//
//= ============== Error ===============//
//= ====================================//

export function getErrorMessage(
    refMessage: string,
    values_message: {
        [key: string]: string | number;
    },
): string {
    let res = '';
    if (refMessage) {
        const err = new errors.ClientError(refMessage, values_message);
        const localErrorMessage = getLocalError(err);
        // console.log('Error', err, err?.clientMessage);
        res = localErrorMessage;
    }
    return res;
}

//= ======================================//
//= ============== Address ===============//
//= ======================================//

export function findStop(stops: Stop[], stopType: StopType): Stop {
    return stops?.find((stop) => stop.stop_type === stopType);
}

export function findStopAddress(stops: Stop[], stopType: StopType): Stop['address'] {
    return findStop(stops, stopType)?.address;
}

export function findStopLocation(stops: Stop[], stopType: StopType): Address['location'] {
    return findStopAddress(stops, stopType)?.location;
}

export function getStopFullname(stop: Stop): string {
    if (!stop?.address?.details) {
        return 'N/A';
    }
    return stop.address.details.full_name ?? generateFullAddress(stop);
}

export function generateFullAddress(stop: Stop): string {
    if (!stop?.address?.details) {
        return 'N/A';
    }
    return `${stop.address.details.street_number} ${stop.address.details.street}, ${stop.address.details.zip_code} ${stop.address.details.city}, ${stop.address.details.country}`;
}

export function prepareStops(stops: Stop[], poi: POI, meetingPoint: MeetingPoint, number = ''): any[] {
    const payloadStops: (PoiStop | Stop)[] = [...stops];
    const pickup = findStop(stops, StopType.PICKUP);
    const keyToDelete = [
        'poi',
        'poi_id',
        'meeting_points',
        'meeting_points_nameBoard',
        'meeting_point_id',
        'force_selection',
    ];

    if (pickup && (poi || meetingPoint)) {
        const pickupWithoutPoi = { ...pickup.address };

        if (!pickupWithoutPoi.details) {
            pickupWithoutPoi.details = {
                street: '',
                street_number: '',
                city: '',
                country: '',
                zip_code: '',
                full_name: poi.name,
            };
        }

        keyToDelete.forEach((key) => {
            delete pickupWithoutPoi?.[key];
        });

        // Overwrite location if meeting point is provided and has a location
        const shouldOverrideLocation = poi && meetingPoint?.location?.lat != null;

        const newPickup: any = {
            address: {
                ...(poi && { poi_id: poi.poi_id }),
                ...(meetingPoint && { meeting_point_id: meetingPoint.id }),
                ...pickupWithoutPoi,
                ...(shouldOverrideLocation && { location: meetingPoint.location }),
            },
            stop_type: StopType.PICKUP,
        };

        if (number) {
            newPickup.connection_number = number;
        }

        payloadStops.splice(payloadStops.indexOf(pickup), 1, newPickup);
    }

    const dropoff = findStop(stops, StopType.DROP_OFF);
    if (dropoff?.address?.poi) {
        const dropoffWithoutPoi = { ...dropoff.address };
        delete dropoffWithoutPoi.poi;
        const newDropoff: any = {
            address: {
                ...(poi && { poi_id: poi.poi_id }),
                ...dropoffWithoutPoi,
            },
            stop_type: StopType.DROP_OFF,
        };
        payloadStops.splice(payloadStops.indexOf(dropoff), 1, newDropoff);
    }

    return payloadStops.sort((a) => (a.stop_type === 'PICKUP' ? -1 : 1));
}

/**
 * Converts a POI (Point of Interest) object to an Address object
 * @param poi - The POI object to convert
 * @returns Address object with POI information
 */
export function poiToAddress(poi: POI): Address {
    if (!poi) {
        // console.trace('poiToAddress: poi is undefined or null');
        return null;
    }

    const baseAddress: Partial<Address> = {
        name: poi.name,
        type: poi.type,
        poi,
    };

    if (poi.details) {
        return {
            ...baseAddress,
            location: poi.location,
            details: poi.details,
        } as Address;
    }

    return {
        ...baseAddress,
        location: poi.meeting_points?.[0]?.location ?? null,
        details: {
            street_number: '',
            street: '',
            city: '',
            country: '',
            zip_code: '',
            full_name: poi.name,
        },
    } as Address;
}

export function getAddressNameAndDescription(details: { name: string; full_name: string }): {
    name: string;
    description: string;
} {
    const isSame: boolean = details.name === details.full_name;
    const { name } = details;
    const description: string = isSame
        ? ''
        : details.name
          ? details.full_name.replace(new RegExp(`${escapeRegExp(details.name)}\\W*?\\s`, ''), '')
          : details.full_name;
    return { name, description };
}

export function getIconFromMeetingPoint(address: Address): IconCommon {
    if (!address?.type) {
        return null;
    }
    switch (address.type) {
        case AddressType.AIRPORT:
            return IconCommon.PlaneTakeOff;
        case AddressType.STATION:
            return IconCommon.RailroadTrain;
        case AddressType.OTHER:
            return IconCommon.PinLocation;
        default:
            return IconCommon.PlaneTakeOff;
    }
}

export function isAirport(stop: Stop): boolean {
    return stop?.address?.type === AddressType.AIRPORT || stop?.address?.poi?.type === AddressType.AIRPORT;
}

export function isStation(stop: Stop): boolean {
    return stop?.address?.type === AddressType.STATION || stop?.address?.poi?.type === AddressType.STATION;
}

export function isOther(stop: Stop): boolean {
    return stop?.address?.type === AddressType.OTHER || stop?.address?.poi?.type === AddressType.OTHER;
}

export function isPUPoi(stops: Stop[]): boolean {
    const pickup = findStop(stops, StopType.PICKUP);
    if (!pickup) {
        return false;
    }
    return isAirport(pickup) || isStation(pickup) || isOther(pickup);
}

// ===================================== //
// ============= Paiement ============== //
// ===================================== //

// On board payment (OBP) comes as its own flag on the payment setting: the group stays centralized
// and default_payment_method stays the company's card (often a wire_transfer one), so neither can be
// used to detect it. Every payment label has to read the flag, or an OBP rider is shown
// "Paiement centralisé".
export function isOnboardPayment(paymentPolicy: PaymentGroupSetting): boolean {
    return !!paymentPolicy?.onboard_payment || paymentPolicy?.default_payment_method?.type === 'onboard_payment';
}

// A ride is OBP either through its own payment method or through the payment setting it was booked
// with — for a centralized group the method stays the company card, so both have to be checked.
export function isRideOnboardPayment(paymentMethod: PaymentMethod, paymentPolicy: PaymentGroupSetting): boolean {
    return (
        !!paymentMethod?.is_onboard_payment ||
        paymentMethod?.type === 'onboard_payment' ||
        isOnboardPayment(paymentPolicy)
    );
}

export function convertPaymentTypePolicy(paymentMethod: PaymentMethod, paymentPolicy: PaymentGroupSetting): string {
    if (isRideOnboardPayment(paymentMethod, paymentPolicy)) {
        return stringsRes.admin.payment.onBoardPayment;
    }

    if (paymentPolicy?.payment_frequency === 'monthly' || paymentPolicy?.payment_mode === 'centralized') {
        return stringsRes.paymentType.centralized;
    }

    switch (paymentMethod?.type) {
        case 'airplus':
            return stringsRes.admin.payment.airplusTitle;
        case 'card_adyen':
        case 'card_ogone':
            return stringsRes.admin.payment.blueCardTitle;
        case 'wire_transfer':
            return stringsRes.admin.payment.wireTransfer;
        case 'sepa':
            return stringsRes.admin.payment.sepa;
        default:
            return '';
    }
}

export function getPaymentDescription(paymentPolicy: PaymentGroupSetting, maskedCard?: string): string {
    if (isOnboardPayment(paymentPolicy)) {
        return stringsRes.paymentType.onboard;
    }

    switch (paymentPolicy?.payment_mode) {
        case 'individual':
            return getMaskedCard(maskedCard) || stringsRes.paymentType.individual;
        case 'centralized':
            return stringsRes.paymentType.centralized;
        default:
            return null;
    }
}

export function getPaymentType(paymentMethod: PaymentMethod, paymentPolicy: PaymentGroupSetting): string {
    if (paymentMethod?.is_onboard_payment) {
        return stringsRes.paymentType.onboard;
    }

    const paymentMode = (paymentMethod as any)?.payment_mode;
    if (paymentMode) {
        return paymentMode === 'unitary' ? stringsRes.paymentType.individual : stringsRes.paymentType.mensual;
    }
    return paymentPolicy?.payment_mode === 'individual'
        ? stringsRes.paymentType.individual
        : stringsRes.paymentType.mensual;
}

export function getShortPaymentString(payment: string): string {
    if (payment === stringsRes.paymentType.individual) {
        return stringsRes.paymentType.short.individual;
    }
    if (payment === stringsRes.paymentType.centralized) {
        return stringsRes.paymentType.short.centralized;
    }
    if (payment === stringsRes.paymentType.onboard) {
        return stringsRes.paymentType.short.onboard;
    }
    return payment;
}

//= ====================================//
//= ============== Voucher ==============//
//= ====================================//

export function formatVoucher(voucher: CoverageVoucher): string {
    const unit =
        voucher?.value_type === 'percentage'
            ? stringsRes.admin.payment.voucher.form.percentageUnit
            : stringsRes.admin.payment.voucher.form.amountUnit;
    return `${voucher?.value ?? ''} ${unit}`;
}

export function buildVoucherPayload(
    contractId: string,
    form: { name: string; amount: string; valueType: CoverageVoucher['value_type'] },
): CreateVoucherInput {
    return {
        contract_id: contractId,
        name: form?.name?.trim(),
        value: Number(`${form?.amount}`.replace(',', '.')),
        value_type: form?.valueType,
    };
}

export function getMaskedCard(card: string, mask: string = stringsRes.booking.cardMask): string {
    if (!card) {
        return card;
    }
    return `${mask}${card.slice(-4)}`;
}

export function isCardValid(card: PaymentCard): boolean {
    return !(card?.expiry_date && new Date() >= new Date(card.expiry_date));
}

export function getRidePrice(price: number, extras = 0, currency: string = stringsRes.booking.euroCurrency): string {
    let priceString = stringsRes.booking.freeRidePrice;
    if (price !== null) {
        priceString = `${(price + extras)?.toFixed(2)} ${currency}`;
    }
    return priceString;
}

// ===================================== //
// ===== Vouchers (prise en charge) ==== //
// ===================================== //

const isPercentageVoucher = (voucher?: QuoteVoucher): boolean => voucher?.value_type === 'percentage';

// Une prise en charge en pourcentage n'a pas de montant dans /quotes, seulement son taux
const isUsableVoucher = (voucher?: QuoteVoucher): boolean =>
    isPercentageVoucher(voucher) ? voucher.value > 0 : voucher?.with_tax > 0;

// L'API peut renvoyer plusieurs prises en charge, une seule s'applique : la plus élevée. Un
// pourcentage ne se compare pas à un montant sans le prix : le premier trouvé est gardé.
export function getBestVoucher(vouchers?: QuoteVoucher[]): QuoteVoucher | null {
    if (!vouchers?.length) {
        return null;
    }
    return vouchers.reduce(
        (best, voucher) =>
            isUsableVoucher(voucher) &&
            (!best || (!isPercentageVoucher(voucher) && !isPercentageVoucher(best) && voucher.with_tax > best.with_tax))
                ? voucher
                : best,
        null as QuoteVoucher | null,
    );
}

// Libellé de la prise en charge quand le montant dépend du service : « 10 % » ou « 30.00 € »
export function formatQuoteVoucher(voucher: QuoteVoucher): string {
    return isPercentageVoucher(voucher)
        ? `${voucher.value} ${stringsRes.admin.payment.voucher.form.percentageUnit}`
        : getRidePrice(voucher?.with_tax);
}

// Part prise en charge par l'entreprise, jamais supérieure au prix (null sans prise en charge).
// Sans extra, c'est la part que ride-manager a calculée et que billing facturera ; le taux n'est
// appliqué ici qu'en son absence ou quand un extra (pancarte) s'ajoute au prix du devis.
export function getCoveredAmount(
    price: number | null,
    voucher?: QuoteVoucher,
    extras = 0,
    breakdown?: PaymentBreakdown,
): number | null {
    if (price === null || price === undefined || !isUsableVoucher(voucher)) {
        return null;
    }
    const total = price + extras;
    const coveredPayers = breakdown?.payers?.filter((payer) => payer.coverage && typeof payer.amount === 'number');
    if (!extras && coveredPayers?.length) {
        return Math.min(
            total,
            coveredPayers.reduce((sum, payer) => sum + payer.amount, 0),
        );
    }
    const covered = isPercentageVoucher(voucher) ? Math.round(total * voucher.value) / 100 : voucher.with_tax;
    return Math.min(total, covered);
}

// Prix restant à charge du client une fois la prise en charge déduite (jamais négatif).
export function getPriceAfterVoucher(
    price: number | null,
    voucher?: QuoteVoucher,
    extras = 0,
    breakdown?: PaymentBreakdown,
): number | null {
    if (price === null || price === undefined) {
        return price ?? null;
    }
    return Math.max(0, price + extras - (getCoveredAmount(price, voucher, extras, breakdown) ?? 0));
}

// La prise en charge est renvoyée au niveau de la réponse /quotes : on la recopie sur chaque
// quote pour qu'elle suive le devis sélectionné dans le store et les écrans suivants.
export function applyVoucherToQuotes(data: GetServicesOutput): GetServicesOutput {
    const voucher = getBestVoucher(data?.vouchers);
    if (!voucher || !data?.quotes?.length) {
        return data;
    }
    return {
        ...data,
        quotes: data.quotes.map((quote) => ({ ...quote, applied_voucher: voucher })),
    };
}

export function getRidePriceWithExtra(ride: Ride, currency: string = stringsRes.booking.euroCurrency): string {
    let priceString = 0;
    // let promoString = '';
    if (isBookingCancelledNoCharge(ride.status) && !ride.price_details?.client_final_price) {
        return `${priceString?.toFixed(2)} ${currency}`;
    }
    if (ride.is_free_ride && !ride.price_details?.client_final_price) {
        return stringsRes.booking.freeRidePrice;
    }
    if (ride.price_details?.client_final_price === null) {
        priceString = ride.price_details.base_price || 0;
    } else {
        priceString = ride.price_details.client_final_price || 0;
    }
    return `${priceString?.toFixed(2)} ${currency}`;
}

// ===================================== //
// ============= Services ============== //
// ===================================== //
export interface Settings {
    eta: boolean;
    price: boolean;
    selectable: boolean;
}

export function isNoDriverAvailable(ride: Ride): boolean {
    return ride?.service?.status === ServiceStatus.NO_DRIVER_AVAILABLE;
}

export function getSetting(serviceStatus: ServiceStatus): Settings {
    let settings = { eta: true, price: true, selectable: true };

    switch (serviceStatus) {
        case ServiceStatus.NO_DRIVER_AVAILABLE:
        case ServiceStatus.TOO_FAR_AWAY:
            settings = { ...settings, eta: false };
            return settings;
        case ServiceStatus.PREBOOK_NOT_ALLOWED:
        case ServiceStatus.ASAP_NOT_ALLOWED:
        case ServiceStatus.INSUFFICIENT_DELAY:
        case ServiceStatus.RIDER_RESTRICTED:
        case ServiceStatus.NO_PRICING_FOR_AREA:
            settings = { ...settings, eta: false, price: false, selectable: false };
            return settings;
        default:
            return settings;
    }
}

export function getQuoteFallback(defaultDispatchGroup: string, quotes: Quote[], isPro: boolean): Quote {
    const newQuote = quotes.find((q) => {
        return q.service.uuid === defaultDispatchGroup;
    });

    if (newQuote && getSetting(newQuote.service.status).selectable) {
        return newQuote;
    }

    let res: Quote = null;
    const indexQuote = quotes.findIndex((q) => {
        return q.service.uuid === defaultDispatchGroup;
    });
    if (isPro) {
        let index = 1;
        while (!res && quotes[(indexQuote + index) % quotes.length] && index < quotes.length) {
            const nextQuote = quotes[(indexQuote + index) % quotes.length];
            if (nextQuote && getSetting(nextQuote.service.status).selectable) {
                res = nextQuote;
            }
            index++;
        }
    } else {
        let index = 1;
        while (!res && quotes[indexQuote + index]) {
            const nextQuote = quotes[indexQuote + index];
            if (nextQuote && getSetting(nextQuote.service.status).selectable) {
                res = nextQuote;
            }
            index++;
        }
    }
    return res;
}

export function isServiceGreen(service: Service): boolean {
    return service?.name.includes('Green') || service?.name.includes('green');
}

export function getEncodedRoute(data: GetServicesOutput | Ride): string {
    if (!data) {
        console.warn('getRoute: services is undefined or null');
        return null;
    }
    return data.encoded_route ?? data.route?.path;
}

// ================================= //
// ============= User ============== //
// ================================= //

export function isRider(ride: Ride): boolean {
    const user = getUser();
    return ride.riders?.some((r) => r.id === user.id);
}

export function isBooker(ride: Ride): boolean {
    const user = getUser();
    return ride.booker?.id === user.id;
}

export function isRiderOrBooker(ride: Ride): boolean {
    return isBooker(ride) || isRider(ride);
}

export function isBookerOnly(ride: Ride): boolean {
    return isBooker(ride) && !isRider(ride);
}

export function isRiderOnly(ride: Ride): boolean {
    return isRider(ride) && !isBooker(ride);
}

export function canRateBooking(ride: Ride): boolean {
    const now = addTimeToDate(0);
    const threshold = addTimeToDate(2, unix(ride.ride_ended_date ?? ride.start_date ?? ride.creation_date), 'month'); // 2 Months
    const isLessThanThreshold = now.isBefore(threshold);
    // const hasComment = ride?.driver_rating?.comment;
    return isBookingFinishedNotCanceled(ride?.status) && isRider(ride) && isLessThanThreshold;
}
export function canSeeBookingRating(ride: Ride): boolean {
    return isBookingFinishedNotCanceled(ride?.status) && isRiderOrBooker(ride);
}

export function isRidePro(ride: Ride): boolean {
    return ride?.group?.contract?.plan?.type === IDContractType.CORPORTATE;
}

export function convertGuestToBaseUser(guests: CompanyGuest[], addGuestType?: boolean): BaseUser[] {
    return (
        guests?.map((g) => {
            const user: BaseUser = {
                id: g.id,
                firstname: g.firstname,
                lastname: g.lastname,
                phone_number: g.phone_e164,
                email: g.email,
            };
            return addGuestType ? { ...user, user_type: 'guest' } : user;
        }) ?? []
    );
}

export function getPriviliegesFromUserId(user: BaseUserId, groupId: string): string[] {
    const group = user?.groups?.find((g) => g.id === groupId);
    return [...new Set(group.roles.map((r) => r.role))];
}

export function privilegesToArray(privileges: string): string[] {
    return privileges?.split(',');
}

export function rolesToArray(roles: string): string[] {
    return roles?.split(',');
}

export function convertUser(user: BaseUserId): UserGroupType {
    const newUser: UserGroupType = {
        ...user,
        privileges: [...new Set(user.groups?.[0]?.roles?.map((r) => r.role))]?.join(','),
        uuid: user.id,
        emails: [{ uuid: '', email: user.email }],
        invite: { status: user.user_type === 'invite' },
        groupscount: 0,
    };
    return newUser;
}

export function convertUserToCoworker(user: BaseUserId): Coworker {
    const coworker: Coworker = {
        ...user,
        groups: user.groups?.map((g) => ({
            ...g,
            uuid: g.id,
            privileges: [...new Set(g.roles.map((r) => r.role))].join(','),
        })),
        uuid: user.id,
        emails: [{ uuid: '', email: user.email }],
        phones: [{ uuid: '', phone_e164: user.phone_number }],
        invite: { status: user.user_type === 'invite', invited_at: user.invited_at },
    };
    return coworker;
}

//= ======================================//
//= ============== Vehicle ===============//
//= ======================================//

/**
 * Get a string of all the infos of the vehicle
 * @param vehicle Vehicle
 * @param short Modal and color / full: registration, brand, model, color
 * @returns
 */
export function getVehicleInfos(vehicle: Vehicle, short = false): string {
    if (!vehicle) {
        return '';
    }
    const parts = short
        ? [vehicle.model, vehicle.color]
        : [vehicle.registration_number, vehicle.brand, vehicle.model, vehicle.color];

    return joinWithDash(...parts);
}

// ================================= //
// ============= Plan ============== //
// ================================= //

export function canCreateUser(plan: Plan, contract: ContractInfos): boolean {
    return plan?.max_users ? contract?.headcount + contract?.invitationcount < plan.max_users : true;
}

export function canCreateGroup(plan: Plan, nbGroup: number): boolean {
    return plan?.max_groups ? nbGroup < plan.max_groups : true;
}

// ======================================== //
// ============= Recurrence =============== //
// ======================================== //

export function recurrenceToString(recurrence: number[]): string {
    return (
        recurrence?.length > 0 &&
        `${stringsRes.time.prefixRecurrence} ${recurrence
            .map((r) => DAYS.find((d) => +d.key === r).value.slice(0, SHORT_DAY_LENGTH))
            .join(', ')}`
    );
}

// ==================================== //
// ============= Quotes =============== //
// ==================================== //
export function getQuoteExpirationDate(quote: Quote): number {
    if (!quote) {
        console.warn('getQuoteExpirationDate: quote is undefined or null');
    }
    return quote?.expiry_date ? quote?.expiry_date * 1000 : quote?.expiracy_date; // expiracy_date is deprecated will be remove in the future
}

// ======================================== //
// ============= SelfService ============== //
// ======================================== //

export function selfServicePolicyChanges(policy: Policy): {
    limitActive: number;
    dayActive: number;
    placeActive: number;
    serviceActive: number;
    costCenterActive: number;
    total: number;
} {
    const lookupTable = {
        limit: [
            'book_for_group_members',
            'book_for_group_guests',
            'reservationLimit',
            'spendingLimit',
            'tip_not_allowed',
            'travel_reason_mandatory',
        ],
    };

    const calculatePlaceActive = () => {
        let res = 0;
        if (policy?.default_pickup_mode) {
            res += 1;
        }
        if (policy?.default_dropoff_mode) {
            res += 1;
        }
        if (policy?.restrictions?.areas?.length > 0) {
            res += 1;
        }
        if (policy?.favorites?.length > 0) {
            res += 1;
        }
        return res;
    };

    const limitActive =
        policy && Object.keys(policy)?.filter((p) => policy[p] && lookupTable.limit.includes(p))?.length;
    const dayActive = policy && Object.keys(policy?.restrictions?.time_slots ?? {})?.length;
    const placeActive = calculatePlaceActive();
    const serviceActive = policy?.dispatch_groups?.filter((el) => el.is_authorized)?.length;
    const costCenterActive = policy?.cost_centers?.length ?? 0;

    return {
        limitActive,
        dayActive,
        placeActive,
        serviceActive,
        costCenterActive,
        total:
            Number(placeActive > 0) +
            Number(dayActive > 0) +
            Number(limitActive > 0) +
            Number(costCenterActive > 0) +
            Number(serviceActive < policy?.dispatch_groups?.length),
    };
}

/**
 * Builds the favorites list a booker actually sees, out of the group policy ones and their own.
 *
 * LT-2077: an admin may flag one group favorite as the "Travail" address. That one wins over the
 * booker's personal work address, which is only hidden here - never deleted - so it comes back as
 * soon as the admin removes the group flag or the user leaves the group. Only WORK is administrable,
 * so a personal HOME address is left untouched whatever the group policy holds.
 */
export function mergeGroupFavorites(groupFavorites: Favorite[] = [], userFavorites: Favorite[] = []): Favorite[] {
    const group = groupFavorites ?? [];
    const groupOwnsWorkAddress = group.some((favorite) => favorite.type === AddressType.WORK);
    const user = (userFavorites ?? []).filter(
        (favorite) => !(groupOwnsWorkAddress && favorite.type === AddressType.WORK),
    );

    return [...group, ...user];
}

/** Stable React key for a favorite: group policy ones carry no uuid. */
export function favoriteKey(favorite: Favorite, index: number): string {
    return favorite?.uuid ?? `${favorite?.type}-${favorite?.details?.full_name ?? ''}-${index}`;
}
